import {
  RiAlertLine,
  RiGpsLine,
  RiLoopLeftLine,
  RiRadarLine,
  RiSimCard2Line,
  RiVolumeMuteLine,
  type RemixiconComponentType,
} from "@remixicon/react";
import { useTranslation } from "react-i18next";
import { Picture } from "~/components/ui/picture";
import { SectionTitle } from "~/components/ui/section-title";
import { cn } from "~/lib/utils";
import accelerometerIcon from "~/assets/products/specs/accelerometer.svg";
import engineIcon from "~/assets/products/specs/engine.svg";
import type { ProductSpecsContent } from "~/data/products/types";

interface SpecsSectionProps {
  content: ProductSpecsContent;
}

type SpecIconName = ProductSpecsContent["items"][number]["icon"];

// Figma's spec icons are Remix Icons apart from engine and accelerometer, which are
// custom and ship as Figma's own SVGs. Figma's exports of the Remix ones are broken.
const SPEC_ICONS: Record<SpecIconName, RemixiconComponentType | string> = {
  updates: RiLoopLeftLine,
  gps: RiGpsLine,
  tamper: RiAlertLine,
  silent: RiVolumeMuteLine,
  cellular: RiRadarLine,
  sim: RiSimCard2Line,
  engine: engineIcon,
  accelerometer: accelerometerIcon,
};

// Bouncie Blue, or Arctic inside the fleet PDP's .fleet scope.
const ICON_COLOR = "text-bouncie-500 fleet:text-bouncie-200";

function SpecIcon({ icon }: { icon: SpecIconName }) {
  const Icon = SPEC_ICONS[icon];
  const className = "size-4 shrink-0 lg:size-6";
  if (typeof Icon === "string")
    // The custom SVGs are filled Bouncie Blue; masking them lets the icon colour fill them.
    return (
      <span
        aria-hidden
        className={cn(ICON_COLOR, className, "bg-current mask-contain mask-center mask-no-repeat")}
        style={{ maskImage: `url("${Icon}")` }}
      />
    );
  return <Icon aria-hidden className={cn(ICON_COLOR, className)} />;
}

// Inside the fleet PDP's .fleet scope the tokens and `fleet:` classes give the dark
// version (Figma 10480:408082); the consumer PDPs are unaffected.
export function SpecsSection({ content }: SpecsSectionProps) {
  const { t } = useTranslation("products");

  return (
    // overflow-x-clip lets the image run past the content edge without a horizontal scrollbar
    <section id="specs" className="scroll-mt-20 overflow-x-clip">
      <div className="wrap lg:fleet:h-[592px] flex flex-col items-start gap-10 py-10 max-lg:relative max-lg:isolate lg:h-[542px] lg:flex-row lg:py-15">
        <div className="flex flex-col items-start gap-10 lg:shrink-0 lg:pt-6">
          <div className="lg:fleet:gap-4 flex flex-col items-start gap-2 lg:gap-3">
            <p className="eyebrow fleet:text-text-heading-alt">
              {t(content.eyebrow)}
            </p>
            <SectionTitle className="text-text-heading lg:whitespace-nowrap">
              {t(content.headline)}
            </SectionTitle>
          </div>
          <ul className="fleet:gap-y-[14.86px] fleet:pb-1 lg:fleet:pb-0 lg:fleet:gap-y-[33.67px] grid grid-cols-1 gap-y-3 md:grid-cols-2 md:gap-x-8 lg:w-[636px] lg:gap-y-8">
            {content.items.map((item) => (
              <li
                key={item.label}
                className={cn(
                  "text-text-body-secondary fleet:min-h-5 lg:fleet:min-h-6 flex min-h-6 items-center gap-2 text-sm leading-5 lg:gap-3 lg:text-lg lg:leading-[1.1]",
                  item.lastInSingleColumn && "max-md:order-last",
                )}
              >
                <SpecIcon icon={item.icon} />
                {t(item.label)}
              </li>
            ))}
          </ul>
        </div>

        {content.image && (
          // Below lg the render sits behind the list's right side: on phones it runs past the
          // screen edge, from sm it's flush with the content's right edge.
          // Desktop: Figma's 730px render sits 144px past the content edge, just below center.
          <div className="w-full max-lg:pointer-events-none max-lg:absolute max-lg:top-[calc(50%+59px)] max-lg:-z-10 max-lg:w-[382px] max-lg:-translate-y-1/2 max-sm:left-[calc(50%+90.5px)] max-sm:-translate-x-1/2 sm:max-lg:right-0 md:max-lg:top-1/2 lg:relative lg:h-full lg:flex-1">
            <Picture
              src={content.image}
              alt={t(content.imageAlt ?? "")}
              className="xl:fleet:top-14 xl:fleet:right-auto xl:fleet:left-px xl:fleet:h-[420px] xl:fleet:w-[498px] xl:fleet:translate-y-0 xl:fleet:object-contain mx-auto h-auto w-full max-w-[730px] lg:absolute lg:top-1/2 lg:right-[-80px] lg:w-[520px] lg:max-w-none lg:translate-y-[calc(-50%+26px)] xl:right-[-144px] xl:w-[730px]"
            />
          </div>
        )}
      </div>
    </section>
  );
}
